import { defineStore } from 'pinia'
import type { User } from '~/types/user'
import { useApi } from '~/api'
import type { Licence } from '~/types/licence'
import type { Site } from '~/types/locations'
import type { MapLocation } from '~/composables/map'
import type { PermissionMap } from '~/types/permissions'
import type { SiteReach } from '~/types/site-scope'

const FALLBACK_ORIGIN: MapLocation = {
  lat: 46.769497734835156,
  lng: -71.30332880520855
}

interface AuthStore {
  accessToken: Ref<string>
  expiresAt: Ref<Date | undefined>
  tokenType: Ref<string | undefined>
  user: Ref<User | undefined>
  permissions: Ref<PermissionMap>
  permissionsLoaded: Ref<boolean>

  isTokenExpired: ComputedRef<boolean>
  isAuthenticated: ComputedRef<boolean>
  hasSessionID: ComputedRef<boolean>

  setUser: (userData: User) => void
  setToken: (token: string, expiration: Date, type: string) => void
  loadCurrentUser: () => Promise<void>
  clearPermissions: () => void
  setPermissions: (perms: PermissionMap) => void
  loadPermissions: () => Promise<void>

  activeLicence: Ref<Licence | undefined>
  lastActiveLicence: Ref<Licence | undefined>
  setActiveLicence: (license: Licence) => void
  clearActiveLicence: () => void
  licenceOrigin: ComputedRef<MapLocation | [number, number]>

  activeSite: Ref<Site | undefined>
  setActiveSite: (site: Site | undefined) => void

  siteReach: Ref<SiteReach | undefined>
  siteReachLoaded: Ref<boolean>
  siteReachFailed: Ref<boolean>
  isSiteScopeEnabled: ComputedRef<boolean>
  loadSiteReach: () => Promise<void>
  clearSiteReach: () => void

  sessionId: Ref<string | undefined>
  setSessionId: (id: string) => void
  clearSessionId: () => void

  $reset: () => void
}

export const useAuthStore = defineStore(
  'authStore',
  (): AuthStore => {
    const accessToken = ref('')
    const expiresAt = ref<Date | undefined>()
    const tokenType = ref<string | undefined>()
    const user = ref<User | undefined>()
    const permissions = ref<PermissionMap>({})
    const permissionsLoaded = ref(false)
    const activeLicence = ref<Licence | undefined>(undefined)
    const lastActiveLicence = ref<Licence | undefined>(undefined)
    const activeSite = ref<Site | undefined>(undefined)
    const siteReach = ref<SiteReach | undefined>(undefined)
    const siteReachLoaded = ref(false)
    const siteReachFailed = ref(false)
    const sessionId = ref<string | undefined>(undefined)

    // Getters
    const isTokenExpired = computed<boolean>((): boolean => {
      return !expiresAt.value || new Date() >= new Date(expiresAt.value)
    })
    const isAuthenticated = computed<boolean>((): boolean => {
      return !!accessToken.value && !isTokenExpired.value
    })
    const licenceOrigin = computed<MapLocation | [number, number]>(
      () => activeLicence.value?.meta_data?.origin ?? FALLBACK_ORIGIN
    )

    const isSiteScopeEnabled = computed<boolean>(() => siteReach.value?.enabled === true || siteReachFailed.value)

    const hasSessionID = computed<boolean>((): boolean => {
      return !!sessionId.value
    })

    const setToken = (token: string, expiration: Date, type: string): void => {
      accessToken.value = token
      expiresAt.value = expiration
      tokenType.value = type
    }

    const setActiveLicence = (license: Licence): void => {
      if (activeLicence.value?.id === license.id) return

      if (activeLicence.value?.id) {
        lastActiveLicence.value = activeLicence.value
      }
      clearPermissions()
      clearSiteReach()
      activeSite.value = undefined

      activeLicence.value = license
    }

    const setActiveSite = (site: Site | undefined): void => {
      activeSite.value = site
    }

    const clearActiveLicence = (): void => {
      lastActiveLicence.value = activeLicence.value
      activeLicence.value = undefined
      activeSite.value = undefined
      clearPermissions()
      clearSiteReach()
    }

    const setUser = (userData: User): void => {
      user.value = userData
    }

    const clearPermissions = (): void => {
      permissions.value = {}
      permissionsLoaded.value = false
    }

    const loadCurrentUser = async (): Promise<void> => {
      const api = useApi()
      user.value = await api.auth.getCurrentUser()
    }

    const setPermissions = (perms: PermissionMap): void => {
      permissions.value = perms
      permissionsLoaded.value = true
    }

    const loadPermissions = async (): Promise<void> => {
      const api = useApi()
      setPermissions(await api.permissions.getPermissions())
    }

    const clearSiteReach = (): void => {
      siteReach.value = undefined
      siteReachLoaded.value = false
      siteReachFailed.value = false
    }

    const loadSiteReach = async (): Promise<void> => {
      const api = useApi()
      let reach: SiteReach
      try {
        reach = await api.permissions.getSiteReach()
      } catch (error) {
        siteReach.value = undefined
        siteReachLoaded.value = false
        siteReachFailed.value = true
        throw error
      }
      siteReach.value = reach
      siteReachLoaded.value = true
      siteReachFailed.value = false

      if (!reach.enabled) {
        activeSite.value = undefined
      }
    }

    const setSessionId = (id: string): void => {
      sessionId.value = id
    }

    const clearSessionId = (): void => {
      sessionId.value = undefined
    }

    const $reset = (): void => {
      accessToken.value = ''
      expiresAt.value = undefined
      tokenType.value = undefined
      activeLicence.value = undefined
      lastActiveLicence.value = undefined
      activeSite.value = undefined
      siteReach.value = undefined
      siteReachLoaded.value = false
      siteReachFailed.value = false
      user.value = undefined
      permissions.value = {}
      permissionsLoaded.value = false
      sessionId.value = undefined
    }

    // const updateUser = async (_userInput: User) => {
    //   // Out of scope for now, to be implemented in a next iteration
    //   // const { user } = await authService.updateUser(userInput)
    //   // this.user = user
    //
    //   return user.value
    // }
    //

    return {
      accessToken,
      expiresAt,
      tokenType,
      user,
      permissions,
      permissionsLoaded,
      isTokenExpired,
      isAuthenticated,
      hasSessionID,
      activeLicence,
      lastActiveLicence,
      activeSite,
      siteReach,
      siteReachLoaded,
      siteReachFailed,
      isSiteScopeEnabled,
      loadSiteReach,
      clearSiteReach,
      licenceOrigin,
      clearActiveLicence,
      setUser,
      setToken,
      setActiveLicence,
      setActiveSite,
      clearPermissions,
      loadCurrentUser,
      setPermissions,
      loadPermissions,
      sessionId,
      setSessionId,
      clearSessionId,
      $reset
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage()
    }
  }
)
